<template>
  <section v-if="photographers.length" class="ni-section ni-photographers" id="photographers">
    <div class="ni-container">
      <SectionHeader
        variant="dark"
        :subtitle="t('newIndex.sections.photographers.subtitle')"
        more-href="/photo_grapher"
      >
        <template #title>
          <span class="ni-section-header__title-mark">
            {{ t('newIndex.sections.photographers.title') }}
          </span>
        </template>
      </SectionHeader>

      <div class="ni-photographers__grid">
        <a
          v-for="item in photographers"
          :key="item.id"
          :id="`photographer-card-${item.id}`"
          class="ni-photographers__card"
          :href="item.href"
          :title="item.name || item.title"
        >
          <div class="ni-photographers__media">
            <img :src="item.thumb" :alt="item.name || item.title" />
          </div>
          <div class="ni-photographers__body">
            <h3 class="ni-photographers__name">{{ item.name || item.title }}</h3>
            <div class="ni-photographers__desc-wrap">
              <p
                class="ni-photographers__desc"
                :class="{ 'is-clamped': overflowIds.has(item.id) }"
                :ref="(el) => setDescRef(item.id, el)"
              >
                <PhotographersMoreIcon v-if="overflowIds.has(item.id)" />{{ item.description }}<PhotographersMoreIcon v-if="!overflowIds.has(item.id)" />
              </p>
            </div>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineComponent, h, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionHeader from './shared/SectionHeader.vue';

const PhotographersMoreIcon = defineComponent({
  name: 'PhotographersMoreIcon',
  setup() {
    return () =>
      h(
        'span',
        { class: 'ni-photographers__more', 'aria-hidden': 'true' },
        [
          h(
            'svg',
            {
              class: 'ni-photographers__more-svg',
              viewBox: '0 0 18 14',
              fill: 'none',
              stroke: 'currentColor',
              'stroke-width': '2.2',
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
            },
            [
              h('path', { d: 'M3 1.5 8.5 7 3 12.5' }),
              h('path', { d: 'M9.5 1.5 15 7 9.5 12.5' }),
            ],
          ),
        ],
      );
  },
});

const props = defineProps({
  photographers: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const descEls = new Map();
const overflowIds = ref(new Set());
let resizeObserver;

function setDescRef(id, el) {
  if (el) descEls.set(id, el);
  else descEls.delete(id);
}

function checkOverflow() {
  const next = new Set();
  descEls.forEach((el, id) => {
    if (el.scrollHeight > el.clientHeight + 1) next.add(id);
  });
  const prev = overflowIds.value;
  if (
    next.size !== prev.size ||
    [...next].some((id) => !prev.has(id))
  ) {
    overflowIds.value = next;
  }
}

onMounted(async () => {
  await nextTick();
  checkOverflow();
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => checkOverflow());
    descEls.forEach((el) => resizeObserver.observe(el));
  }
});

watch(
  () => props.photographers,
  async () => {
    await nextTick();
    checkOverflow();
    if (resizeObserver) {
      resizeObserver.disconnect();
      descEls.forEach((el) => resizeObserver.observe(el));
    }
  },
  { deep: true },
);

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>
